const GENERAL = 'General';
const STABILITY = 'Stability';
const PERFORMANCE = 'Performance';
const INFRASTRUCTURE = 'Infrastructure';

export const ALERT_CODE = {
   STREAM_OFFLINE: '21001',
   CONTINUOUS_RESTARTS: '22000',
   CONTINUOUS_RESTARTS_POOR_PERFORMANCE: '22001',
   INACTIVE_INGESTS: '22010',
   INACTIVE_AND_ACTIVE_INGESTS: '22011',
   STR_SUBOPTIMAL_PERFORMANCE: '23001',
   STR_POOR_PERFORMANCE: '23002',
   DUPLICATED_INGEST: '24100',
   DUPLICATED_INGEST_DIFFERENT_INFRASTRUCTURES: '24101',
   DUPLICATED_INGEST_DIFFERENT_LOCATIONS: '24102',
   DUPLICATED_INGEST_DIFFERENT_IPS: '24103',
};

const ALERT_CATEGORY_DESCRIPTION_MAPPER = {
   [GENERAL]: 'General alerts include basic issues related to unexpected ingest behavior.',
   [STABILITY]:
      'Stream issues that may appear as a consequence of irregular communication problems with the nanoStream Cloud can be identified through stability alerts. Therefore the stream restart attempts as well as the active connections are analysed.',
   [PERFORMANCE]:
      'Either bandwidth issues or insufficient encoder/computing performances of the ingest source could be observed.',
   [INFRASTRUCTURE]:
      'Infrastructure alerts represent issues that may be invisble from the outside of our infrastructure and even if there are no direct impairments stream performance/quality wise, these issues can lead to critical problems. Alerts of this category serve especially as early notification system, so taking action is immediately possible. Occuring problems with the underlying Infrastructure of the corresponding stream are detected by analysing just the passthrough (non-transcoding) streams. Finding multiple references on different server instances fires a Duplicated Ingest Alert. Depending on the passthrough ingest location or source, the specific alert varies slightly.',
};

//    title => custom widget title
//    alertInfo => custom info for widget hint top right corner
//    category => code category definition from docs
//    categoryDescription => corresponding description from docs
//    recommendation => code specific recommended action
//    custom => special fields tied to a specific alert code; structure can differ;

export const ALERT_MAPPER = {
   [ALERT_CODE.STREAM_OFFLINE]: {
      title: '',
      alertInfo: 'This stream showed unexpected ingest behavior.',
      category: 'General Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[GENERAL],
      recommendation: 'Restart the ingest stream.',
   },
   [ALERT_CODE.CONTINUOUS_RESTARTS]: {
      title: 'Continous Restarts',
      alertInfo: 'This stream did restart too often within 5 minutes.',
      category: 'Stability Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[STABILITY],
      recommendation: 'Restart the ingest stream.',
      metricPath: 'activity', // path to fetch rtmp stats regarding elapsed time between restarts
      maxRtmpStatDisplayRangeInMS: 60 * 60 * 1000, // 1 hour in milliseconds,
   },
   [ALERT_CODE.CONTINUOUS_RESTARTS_POOR_PERFORMANCE]: {
      title: 'Continous Restarts with poor performance',
      alertInfo: 'This stream did restart too often within 5 minutes with poor performance.',
      category: 'Stability Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[STABILITY],
      recommendation: 'Restart the ingest stream.',
      metricPath: 'activity', // path to fetch rtmp stats regarding elapsed time between restarts
      maxRtmpStatDisplayRangeInMS: 60 * 60 * 1000, // 1 hour in milliseconds,
   },
   [ALERT_CODE.INACTIVE_INGESTS]: {
      title: 'Inactive Ingest',
      alertInfo: 'The ingest stream is connected but not actively sending data.',
      category: 'Stability Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[STABILITY],
      recommendation:
         'Check your encoder or streaming source configuration. Ensure the source is actively sending data to the stream.',
   },
   [ALERT_CODE.INACTIVE_AND_ACTIVE_INGESTS]: {
      title: 'Inactive and Active Ingests',
      alertInfo: 'The ingest stream has both inactive and active connections.',
      category: 'Stability Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[STABILITY],
      recommendation:
         'Check your encoder or streaming source configuration. Some connections are not actively sending data while others are. Close the inactive connections.',
   },
   [ALERT_CODE.STR_SUBOPTIMAL_PERFORMANCE]: {
      title: 'Stream Time Ratio (STR) Suboptimal Performance',
      alertInfo:
         'The average stream time ratio (STR) of this stream was between 0.93 and 0.86 for longer than 1 minute.',
      category: 'Performance Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[PERFORMANCE],
      recommendation: 'Restart the ingest stream.',
      metricPath: 'streamTimeRatio',
      isExtendedDatapointRequired: true,
      maxRtmpStatDisplayRangeInMS: 60 * 60 * 1000 * 2, // 2 hours in milliseconds,
   },
   [ALERT_CODE.STR_POOR_PERFORMANCE]: {
      title: 'Stream Time Ratio (STR) Poor Performance',
      alertInfo: 'The average stream time ratio (STR) of this stream was below 0.86 for longer than 1 minute.',
      category: 'Performance Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[PERFORMANCE],
      recommendation: 'Restart the ingest stream.',
      metricPath: 'streamTimeRatio',
      isExtendedDatapointRequired: true,
      maxRtmpStatDisplayRangeInMS: 60 * 60 * 1000 * 2, // 2 hours in milliseconds,
   },
   [ALERT_CODE.DUPLICATED_INGEST]: {
      title: 'Duplicated Ingests',
      alertInfo: 'More than 1 ingest source detected.',
      category: 'Infrastructure Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[INFRASTRUCTURE],
      recommendation:
         'Check your ingest sources and insure, that only 1 source is ingesting to this stream name. Restart the ingest stream, if necessary.',
      custom: {
         propLabel: 'Unknown',
      },
   },
   [ALERT_CODE.DUPLICATED_INGEST_DIFFERENT_INFRASTRUCTURES]: {
      title: 'Duplicated Ingests (Multiple Infrastructures)',
      alertInfo: 'This stream is meant to ingest via WebRTC protocol, but ingests via rtmp instead.',
      category: 'Infrastructure Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[INFRASTRUCTURE],
      recommendation:
         'Check your ingest sources and insure, that only 1 source is ingesting to this stream name. Restart the ingest stream, if necessary.',
      custom: {
         propLabel: 'Infrastructure',
      },
   },
   [ALERT_CODE.DUPLICATED_INGEST_DIFFERENT_LOCATIONS]: {
      title: 'Duplicated Ingests (Multiple Locations)',
      alertInfo: 'More than 1 ingest source detected, ingesting from multiple geographic locations.',
      category: 'Infrastructure Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[INFRASTRUCTURE],
      recommendation:
         'Check your ingest sources and insure, that only 1 source is ingesting to this stream name. Restart the ingest stream, if necessary.',
      connectionProp: 'city',
      custom: {
         propLabel: 'Location',
      },
   },
   [ALERT_CODE.DUPLICATED_INGEST_DIFFERENT_IPS]: {
      title: 'Duplicated Ingests (Multiple IPs)',
      alertInfo: 'More than 1 ingest source detected, ingesting from multiple IPs.',
      category: 'Infrastructure Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[INFRASTRUCTURE],
      recommendation:
         'Check your ingest sources and insure, that only 1 source is ingesting to this stream name. Restart the ingest stream, if necessary.',
      connectionProp: 'ip',
      custom: {
         propLabel: 'IPv4 Address',
      },
   },
};
